import { ConfiguracaoAvaliacao } from '../../../core/config/services/avaliacao.service';
import { DateUtils } from '../../../shared/date-utils';
import { LinhaDesempenho, NotaView, Periodo } from '../models/nota-types';

interface EncerramentoTurma {
  tipoPeriodo?: string;
  identificadorExameFinal?: string;
  status?: string;
  periodoId?: number;
  periodoAvaliativo?: { id: number } | number;
}

/**
 * Resolve qual `NotaView` deve ser salva a partir de uma linha da grade: se o
 * próprio item já é avaliável, retorna ele mesmo; senão, cai pra nota de
 * média do período ou pra primeira nota da linha.
 */
export function resolveNotaParaSalvar(
  nota: NotaView | (LinhaDesempenho & { notaMediaPeriodo?: NotaView; notas?: NotaView[] })
): NotaView | null {
  if ((nota as NotaView).avaliavel) {
    return nota as NotaView;
  }

  const linha = nota as LinhaDesempenho & { notaMediaPeriodo?: NotaView; notas?: NotaView[] };
  return linha.notaMediaPeriodo || (linha.notas && linha.notas[0]) || null;
}

/**
 * Resolve o modo de avaliação de uma nota (NUMERICO/DESCRITIVO/CONCEITO/CONCEITO_NUMERICO),
 * na mesma ordem de prioridade do `ed-input-nota` do AngularJS. Efeito colateral: cacheia
 * o resultado em `nota.$$modoAvaliacao` (mesmo comportamento de antes da extração).
 */
export function resolveModoAvaliacaoNota(nota: NotaView): string {
  if (nota.$$modoAvaliacao && nota.$$modoAvaliacao !== 'NUMERICO') {
    return nota.$$modoAvaliacao;
  }

  const enturmacaoExt = nota.enturmacao as {
    avaliacaoDescritiva?: boolean;
    matricula?: { avaliacaoDescritiva?: boolean };
  } | undefined;
  const avalDescritiva = enturmacaoExt?.avaliacaoDescritiva || enturmacaoExt?.matricula?.avaliacaoDescritiva;
  if (avalDescritiva) {
    nota.$$modoAvaliacao = 'DESCRITIVO';
    return 'DESCRITIVO';
  }

  if (nota.avaliavel?.modoAvaliacao) {
    nota.$$modoAvaliacao = String(nota.avaliavel.modoAvaliacao);
    return nota.$$modoAvaliacao;
  }

  if (nota.notaDescritiva) {
    nota.$$modoAvaliacao = 'DESCRITIVO';
    return 'DESCRITIVO';
  }

  if (nota.notaConceito || (nota.$$nota && typeof nota.$$nota === 'object')) {
    nota.$$modoAvaliacao = 'CONCEITO_NUMERICO';
    return 'CONCEITO_NUMERICO';
  }

  return 'NUMERICO';
}

export function extractNotaConceitoValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object') {
    const sigla = (value as { sigla?: unknown }).sigla;
    if (typeof sigla === 'string') {
      return sigla;
    }
  }

  return String(value || '');
}

export function getConceitosConfigurados(
  configuracaoAvaliacao: ConfiguracaoAvaliacao,
  modoAvaliacao?: string
): Array<Record<string, unknown>> {
  const configuracao = configuracaoAvaliacao as unknown as Record<string, unknown>;

  if (modoAvaliacao === 'CONCEITO_NUMERICO') {
    const avaliacaoConceitoNumerico = configuracao['avaliacaoConceitoNumerico'] as Record<string, unknown> | undefined;
    const conceitos = avaliacaoConceitoNumerico?.['conceitos'];
    const conceitosNumericos = avaliacaoConceitoNumerico?.['conceitosNumericos'];

    if (Array.isArray(conceitos) && conceitos.length) {
      return conceitos as Array<Record<string, unknown>>;
    }

    if (Array.isArray(conceitosNumericos) && conceitosNumericos.length) {
      return conceitosNumericos as Array<Record<string, unknown>>;
    }
  }

  const avaliacaoConceito = configuracao['avaliacaoConceito'] as Record<string, unknown> | undefined;
  const conceitos = avaliacaoConceito?.['conceitos'];

  return Array.isArray(conceitos) ? conceitos as Array<Record<string, unknown>> : [];
}

export function findConceitoConfigurado(
  sigla: string,
  configuracaoAvaliacao: ConfiguracaoAvaliacao,
  modoAvaliacao?: string
): Record<string, unknown> | undefined {
  const valorSigla = String(sigla || '').trim();
  if (!valorSigla) {
    return undefined;
  }

  return getConceitosConfigurados(configuracaoAvaliacao, modoAvaliacao)
    .find((conceito) => String(conceito['sigla'] || conceito['descricao'] || '').trim() === valorSigla);
}

export function resolveNotaConceitoValue(
  nota: NotaView,
  configuracaoAvaliacao: ConfiguracaoAvaliacao,
  modoAvaliacao?: string
): NotaView['notaConceito'] | undefined {
  const valor = nota.$$nota;
  if (valor === null || valor === undefined || valor === '') {
    return undefined;
  }

  if (valor && typeof valor === 'object') {
    return { ...(valor as Record<string, unknown>) };
  }

  const sigla = extractNotaConceitoValue(valor);
  const conceitoConfigurado = findConceitoConfigurado(sigla, configuracaoAvaliacao, modoAvaliacao || nota.avaliavel?.modoAvaliacao);
  return conceitoConfigurado || sigla;
}

/**
 * Converte o valor de $$nota para os campos corretos baseado no modo de avaliação.
 * Igual ao comportamento do ed-input-nota do AngularJS. Muta `nota` diretamente
 * (mesmo comportamento de antes da extração).
 */
export function convertNotaValue(nota: NotaView, configuracaoAvaliacao: ConfiguracaoAvaliacao): void {
  if (nota.$$nota === null || nota.$$nota === undefined || nota.$$nota === '') {
    nota.notaNumerica = undefined;
    nota.notaDescritiva = undefined;
    nota.notaConceito = undefined;
    return;
  }

  const modoAvaliacao = resolveModoAvaliacaoNota(nota);

  nota.notaNumerica = undefined;
  nota.notaDescritiva = undefined;
  nota.notaConceito = undefined;

  switch (modoAvaliacao) {
    case 'NUMERICO': {
      const valorNumerico = parseFloat(String(nota.$$nota).replace(',', '.'));
      nota.notaNumerica = isNaN(valorNumerico) ? undefined : valorNumerico;
      break;
    }
    case 'CONCEITO':
    case 'CONCEITO_NUMERICO':
      nota.notaConceito = resolveNotaConceitoValue(nota, configuracaoAvaliacao, modoAvaliacao);
      break;
    case 'DESCRITIVO':
      nota.notaDescritiva = String(nota.$$nota);
      break;
    default: {
      // Modo não identificado: tenta numérico antes de descritivo.
      const valor = parseFloat(String(nota.$$nota).replace(',', '.'));
      if (!isNaN(valor)) {
        nota.notaNumerica = valor;
      } else {
        nota.notaDescritiva = String(nota.$$nota);
      }
    }
  }
}

/**
 * Limpa campos de nota que não pertencem ao modo de avaliação atual (ex: ao
 * mudar pra DESCRITIVO, zera notaNumerica/notaConceito). Muta `nota` diretamente
 * (mesmo comportamento de antes da extração).
 */
export function clearFields(nota: NotaView): void {
  const tipoAvaliacao = resolveModoAvaliacaoNota(nota);

  if (!nota.id) {
    delete nota.id;
  }

  const MODO_AVALIACAO: { [key: string]: string[] } = {
    DESCRITIVO: ['notaNumerica', 'notaConceito'],
    NUMERICO: ['notaDescritiva', 'notaConceito'],
    CONCEITO: ['notaNumerica', 'notaDescritiva'],
    CONCEITO_NUMERICO: ['notaNumerica', 'notaDescritiva']
  };

  if (MODO_AVALIACAO[tipoAvaliacao]) {
    MODO_AVALIACAO[tipoAvaliacao].forEach((tipo: string) => {
      (nota as unknown as { [key: string]: unknown })[tipo] = undefined;
    });
  }
}

export function getSituacaoDescricaoCurta(situacao?: string): string {
  const value = String(situacao || '').toUpperCase();
  const labels: Record<string, string> = {
    DISPENSADO: 'Dispensado',
    DISPENSADO_PARCIAL: 'Dispensado parcial',
    REMANEJADO: 'Remanejado(a) internamente',
    TRANSFERIDO: 'Transferido(a)'
  };
  return labels[value] || 'bloqueada';
}

/**
 * Data de publicação de uma avaliação (resultado + parecer) pra Pais/Alunos,
 * usada no modal de confirmação de publicação.
 */
export function getDataPublicacaoAvaliacao(nota: NotaView | null): string | null {
  return nota?.avaliavel?.publicavel?.dataPublicacao || nota?.avaliavel?.dataPublicacao || null;
}

/**
 * Rótulo de exibição de uma avaliação no modal de confirmação de publicação
 * (descrição, sigla ou tipo de período, na ordem de preferência).
 */
export function getRotuloAvaliacaoPublicacao(nota: NotaView | null): string {
  return String(nota?.avaliavel?.descricao || nota?.avaliavel?.sigla || nota?.avaliavel?.tipoPeriodo || 'avaliação');
}

/**
 * Mensagem de sucesso exibida após publicar/desfazer a publicação de uma avaliação.
 */
export function getMensagemSucessoPublicacaoAvaliacao(nota: NotaView | null, publicado: boolean): string {
  if (publicado) {
    return `O resultado e o parecer de ${getRotuloAvaliacaoPublicacao(nota)} foram publicados com sucesso para Pais e Alunos.`;
  }

  return `A publicação de ${getRotuloAvaliacaoPublicacao(nota)} foi desfeita com sucesso.`;
}

export function isNotaMediaPeriodo(nota: NotaView): boolean {
  return nota.avaliavel?.tipoPeriodo === 'MEDIA_PERIODO';
}

/**
 * Descrição de um período avaliativo pra exibição, incluindo o intervalo de
 * datas quando disponível (ex.: "Bimestre 1 (01/01 até 31/03)").
 */
export function formatPeriodoDescricao(periodo: Periodo): string {
  if (!periodo) return '';

  // DateUtils.formatarDataBrasileira extrai dd/mm/aaaa direto da string ISO,
  // sem passar por `new Date(...)` — evita o bug de timezone em que
  // "2025-01-01" (interpretado como UTC meia-noite) desloca pro dia anterior
  // em fusos UTC- (ex.: UTC-3, Brasil).
  const dataInicial = periodo.dataInicial ? DateUtils.formatarDataBrasileira(periodo.dataInicial) : '';
  const dataFinal = periodo.dataFinal ? DateUtils.formatarDataBrasileira(periodo.dataFinal) : '';

  if (dataInicial && dataFinal) {
    return `${periodo.descricao} (${dataInicial} até ${dataFinal})`;
  }

  return periodo.descricao || '';
}

/**
 * Trunca um texto pro tamanho máximo indicado, acrescentando reticências.
 */
export function limitText(value: string | null | undefined, maxLength = 50): string {
  const text = String(value || '').trim();

  if (!text) {
    return '';
  }

  return text.length > maxLength
    ? `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`
    : text;
}

function matchesExameFinal(
  enc: EncerramentoTurma,
  periodo: Periodo & { identificadorExameFinal?: string }
): boolean {
  return !!(periodo.identificadorExameFinal &&
            enc.identificadorExameFinal === periodo.identificadorExameFinal);
}

function matchesPeriodoAvaliativo(enc: EncerramentoTurma, periodoId: number): boolean {
  if (typeof enc.periodoAvaliativo === 'object' && enc.periodoAvaliativo?.id === periodoId) {
    return true;
  }
  if (typeof enc.periodoAvaliativo === 'number' && enc.periodoAvaliativo === periodoId) {
    return true;
  }
  return enc.periodoId === periodoId;
}

/**
 * Verifica se um registro de encerramento de turma corresponde a um período
 * avaliativo específico. Baseado na lógica do AngularJS
 * desempenho-periodo-view.service.js: EXAME_FINAL compara pelo
 * identificador do exame, MODULO basta o status ENCERRADO, os demais
 * comparam pelo período avaliativo/tipo de período.
 */
export function matchesEncerramento(enc: EncerramentoTurma, periodo: Periodo): boolean {
  if (enc.status !== 'ENCERRADO') {
    return false;
  }

  if (enc.tipoPeriodo === 'EXAME_FINAL') {
    return matchesExameFinal(enc, periodo);
  }

  if (enc.tipoPeriodo === 'MODULO') {
    return true;
  }

  return matchesPeriodoAvaliativo(enc, periodo.id);
}

/**
 * Verifica se o período letivo (ano) como um todo está encerrado — usado
 * pros tipos de período vinculados ao ano inteiro (MEDIA_FINAL,
 * CONSELHO_CLASSE, EXAME_FINAL sem período avaliativo específico).
 */
export function isPeriodoLetivoEncerrado(encerramentos: Array<Record<string, unknown>>): boolean {
  return encerramentos.some((enc) => {
    const status = String(enc['status'] || '');
    const tipoPeriodo = String(enc['tipoPeriodo'] || '');
    return status === 'ENCERRADO' && (
      tipoPeriodo === 'MODULO' ||
      tipoPeriodo === 'PERIODO_LETIVO' ||
      tipoPeriodo === 'ANO'
    );
  });
}

/**
 * Seleciona automaticamente o período avaliativo: 1) o período que contém a
 * data atual; 2) se nenhum contiver, o último período (mais recente por
 * data final).
 */
export function selectPeriodoAutomatico(periodos: Periodo[]): Periodo | null {
  if (!periodos || periodos.length === 0) return null;

  const dataAtual = new Date();
  dataAtual.setHours(0, 0, 0, 0);

  const periodosOrdenados = [...periodos].sort((a, b) => {
    const dataFinalA = a.dataFinal ? new Date(a.dataFinal) : new Date(0);
    const dataFinalB = b.dataFinal ? new Date(b.dataFinal) : new Date(0);
    return dataFinalB.getTime() - dataFinalA.getTime();
  });

  for (const periodo of periodosOrdenados) {
    if (periodo.dataInicial && periodo.dataFinal) {
      const dataInicial = new Date(periodo.dataInicial);
      const dataFinal = new Date(periodo.dataFinal);
      dataInicial.setHours(0, 0, 0, 0);
      dataFinal.setHours(23, 59, 59, 999);

      if (dataAtual >= dataInicial && dataAtual <= dataFinal) {
        return periodo;
      }
    }
  }

  return periodosOrdenados[0];
}

/**
 * Uma nota tem conteúdo publicável quando há algum valor preenchido
 * (numérica, conceito, descritiva ou parecer), independente do modo de
 * avaliação.
 */
export function isNotaComConteudoPublicavel(nota: NotaView): boolean {
  const conceito = extractNotaConceitoValue(nota.notaConceito);
  const notaDescritiva = String(nota.notaDescritiva || '').trim();
  const parecer = String((nota as { parecerDetalhadoConteudo?: string; parecer?: string }).parecerDetalhadoConteudo || (nota as { parecer?: string }).parecer || '').trim();

  return nota.notaNumerica !== null && nota.notaNumerica !== undefined
    || !!conceito
    || !!notaDescritiva
    || !!parecer;
}

export function isAvaliacaoPublicada(nota: NotaView): boolean {
  return !!(nota.avaliavel?.publicavel?.publicado ?? nota.avaliavel?.publicado);
}

/**
 * Verifica se a data do avaliável é posterior à data fim da enturmação do
 * aluno (nesse caso, a nota não deveria contar pra esse aluno).
 */
export function verificaDataFimEnturmacao(nota: NotaView): boolean {
  if (!nota.avaliavel?.data) return false;
  const enturmacaoDataFim = (nota.enturmacao as { dataFim?: string })?.dataFim;
  if (!enturmacaoDataFim) return false;

  const dataItem = new Date(nota.avaliavel.data);
  const dataFim = new Date(enturmacaoDataFim);

  return dataItem > dataFim;
}

export function resolveNumeroChamada(linha: LinhaDesempenho): number {
  return Number(linha.enturmacao?.numeroChamada || 0);
}

export function resolveNomeAluno(linha: LinhaDesempenho): string {
  const matricula = (linha.enturmacao?.matricula || {}) as { nome?: string; nomeSocial?: string };
  return String(matricula.nomeSocial || matricula.nome || '');
}

/**
 * Decide se o botão/ação de parecer deve ser exibido: a `condicao` externa
 * (quando explicitamente `false`) tem prioridade; senão, respeita a
 * configuração de visibilidade do parecer.
 */
export function deveExibirParecer(condicao: boolean | undefined, configuracaoAvaliacao: ConfiguracaoAvaliacao): boolean {
  return Boolean(
    (condicao !== false) &&
    (configuracaoAvaliacao.PARECER?.visivel !== false)
  );
}
